import { useEffect, useState } from "react";
import { Archive, ArchiveRestore, CalendarDays, Copy, Info, Loader2, Mail, Phone, Save, Trash2, Undo2, Video, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import ActivitiesSection from "./ActivitiesSection";
import NotesSection from "./NotesSection";
import RescheduleControl from "./RescheduleControl";
import {
  ADMIN_BOOKING_STATUSES,
  ADMIN_BOOKING_STATUS_LABELS,
  ACTIVITY_TYPES,
  ACTIVITY_TYPE_LABELS,
  EVENT_TYPE_LABELS,
  PIPELINE_HINT,
  PIPELINE_LABELS,
  PIPELINE_STATUSES,
  PROTOCOL_LABELS,
  PROVENANCE_LABELS,
} from "./labels";

const PROTOCOL_OPTIONS = ["GENERAL", "AC_START", "AC_HUNT", "AC_SELECT", "AC_INVEST", "AC_MANAGEMENT"];

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "";
  }
};

const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch {
    return iso || "";
  }
};

function BookingCard({ booking, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const patch = async (payload, message) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/bookings/${booking.id}`, payload);
      toast.success(message);
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#c9a45c]/18 bg-[#141414] p-4" data-testid={`drawer-booking-${booking.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-serif text-[15px] text-gold-light" data-testid={`drawer-booking-slot-${booking.id}`}>
            <CalendarDays size={12} className="mr-1.5 inline text-gold" />
            {String(booking.date || "").split("-").reverse().join("/")} · ore {booking.time} · {booking.duration_minutes} min
          </p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.1em] text-[#a8a5a0]">
            {PROTOCOL_LABELS[booking.protocol_interest] || booking.protocol_interest}
            {booking.source ? ` · da ${PROVENANCE_LABELS[booking.source] || booking.source}` : ""}
          </p>
        </div>
        <select
          className="field !w-auto !py-1.5 !text-[11px]"
          value={booking.status}
          disabled={busy}
          onChange={(e) => patch({ status: e.target.value }, "Stato appuntamento aggiornato")}
          data-testid={`drawer-booking-status-${booking.id}`}
        >
          {ADMIN_BOOKING_STATUSES.map((s) => (
            <option key={s} value={s}>{ADMIN_BOOKING_STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>
      {(booking.selected_goal || booking.selected_type) && (
        <p className="mt-2 text-[12px] text-[#dedbd4]">
          {booking.selected_goal}
          {booking.selected_type ? ` · ${booking.selected_type}` : ""}
        </p>
      )}
      {booking.situation && <p className="mt-1 text-[12px] italic text-[#bdb8ad]">“{booking.situation}”</p>}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px]">
        {booking.meet_link && booking.status === "confermata" && (
          <a href={booking.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-gold-light hover:underline" data-testid={`drawer-booking-meet-${booking.id}`}>
            <Video size={12} className="text-gold" /> Entra nella video-call
          </a>
        )}
        {booking.status !== "annullata" && booking.status !== "da_riprogrammare" && (
          <button
            onClick={() => setRescheduling((v) => !v)}
            className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] underline-offset-2 hover:text-gold-light hover:underline"
            data-testid={`drawer-booking-reschedule-${booking.id}`}
          >
            Riprogramma
          </button>
        )}
        {booking.status !== "annullata" && (
          confirmCancel ? (
            <span className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.1em] text-[#e07a5f]">Confermi l'annullamento?</span>
              <button
                onClick={() => patch({ status: "annullata" }, "Appuntamento annullato")}
                className="border border-[#e07a5f]/50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#e07a5f]"
                data-testid={`drawer-booking-cancel-confirm-${booking.id}`}
              >
                Sì, annulla
              </button>
              <button onClick={() => setConfirmCancel(false)} className="text-[10px] uppercase tracking-[0.12em] text-[#929292]" data-testid={`drawer-booking-cancel-no-${booking.id}`}>
                No
              </button>
            </span>
          ) : (
            <button
              onClick={() => setConfirmCancel(true)}
              className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] underline-offset-2 hover:text-[#e07a5f] hover:underline"
              data-testid={`drawer-booking-cancel-${booking.id}`}
            >
              Annulla appuntamento
            </button>
          )
        )}
      </div>
      {rescheduling && (
        <RescheduleControl
          booking={booking}
          onDone={(data) => {
            setRescheduling(false);
            onUpdated(data);
          }}
          onCancel={() => setRescheduling(false)}
        />
      )}
    </div>
  );
}

export default function ContactDrawer({ contactId, focusActivityId = null, onClose, onChanged }) {
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", protocol_interest: "GENERAL" });
  const [outcomeForm, setOutcomeForm] = useState({ consultation_outcome: "", next_step: "" });
  const [outcomeActivity, setOutcomeActivity] = useState({ type: "chiamata", due_date: "", due_time: "" });
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    setContact(null);
    setConfirmDelete(false);
    api
      .get(`/contacts/${contactId}`)
      .then(({ data }) => {
        setContact(data);
        setForm({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          protocol_interest: data.protocol_interest || "GENERAL",
        });
        setOutcomeForm({ consultation_outcome: data.consultation_outcome || "", next_step: data.next_step || "" });
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [contactId]);

  const apply = (data) => {
    setContact((c) => ({ ...c, ...data }));
    onChanged();
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/contacts/${contactId}`, form);
      toast.success("Scheda cliente aggiornata");
      apply(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changePipeline = async (status) => {
    try {
      const { data } = await api.patch(`/contacts/${contactId}`, { pipeline_status: status });
      apply(data);
      toast.success("Percorso clienti aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const action = async (path, message) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contacts/${contactId}/${path}`);
      toast.success(message);
      apply(data);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/contacts/${contactId}`);
      toast.success("Contatto spostato nel cestino (30 giorni)");
      apply(data);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveOutcome = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/contacts/${contactId}`, outcomeForm);
      apply(data);
      // Se è stata indicata una data, programma anche l'attività collegata (senza duplicati)
      const { due_date, due_time, type } = outcomeActivity;
      if (due_date && due_time) {
        const desc = (outcomeForm.next_step || "").trim() || "Prossimo passo programmato";
        const duplicate = (contact.activities || []).some(
          (a) => a.status === "aperta" && a.due_date === due_date && a.due_time === due_time && (a.description || "") === desc
        );
        if (duplicate) {
          toast.info("Attività già programmata per questa data: nessun duplicato creato");
        } else {
          const res = await api.post(`/contacts/${contactId}/activities`, { type, due_date, due_time, description: desc });
          apply(res.data);
          if (res.data.duplicate) {
            toast.info("Attività già programmata per questa data: nessun duplicato creato");
          } else {
            toast.success("Attività programmata per il prossimo passo");
          }
        }
      } else {
        toast.success("Esito e prossimo passo salvati");
      }
      setOutcomeActivity({ type: "chiamata", due_date: "", due_time: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyContacts = async () => {
    const text = [contact?.name, contact?.phone, contact?.email].filter(Boolean).join(" · ");
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Recapiti copiati negli appunti");
    } catch {
      toast.error(`Copia non riuscita. Recapiti: ${text}`);
    }
  };

  const reachedStages = (() => {
    if (!contact) return [];
    const map = {};
    (contact.events || [])
      .filter((e) => e.type === "fase")
      .forEach((e) => {
        const target = (e.text.split("→")[1] || e.text.split("Fase raggiunta:")[1] || "").trim();
        const status = Object.keys(PIPELINE_LABELS).find((k) => PIPELINE_LABELS[k] === target);
        if (status && !map[status]) map[status] = e.at;
      });
    if (contact.pipeline_status && !map[contact.pipeline_status]) map[contact.pipeline_status] = contact.created_at;
    return Object.entries(map)
      .map(([status, at]) => ({ status, at }))
      .sort((a, b) => PIPELINE_STATUSES.indexOf(a.status) - PIPELINE_STATUSES.indexOf(b.status));
  })();

  const timeline = (() => {
    if (!contact) return [];
    const items = [
      { at: contact.created_at, label: "Contatto creato", type: "contatto_creato" },
      ...(contact.events || [])
        .filter((e) => e.type === "fase" || e.type === "amministrativo")
        .map((e) => ({ at: e.at, label: e.text, type: e.type })),
      ...(contact.leads || []).map((l) => ({ at: l.created_at, label: `Richiesta dal form — obiettivo: ${l.goal}`, type: "richiesta" })),
      ...(contact.bookings || []).map((b) => ({
        at: b.created_at,
        label: `Videoconsulenza prenotata — ${String(b.date || "").split("-").reverse().join("/")} ore ${b.time} (${ADMIN_BOOKING_STATUS_LABELS[b.status] || b.status})`,
        type: "prenotazione",
      })),
      ...(contact.notes || []).map((n) => ({ at: n.created_at, label: `Nota: ${n.text}`, type: "nota" })),
      ...(contact.activities || []).map((a) => ({
        at: a.status === "completata" && a.completed_at ? a.completed_at : a.created_at,
        label:
          a.status === "completata"
            ? `Attività completata: ${ACTIVITY_TYPE_LABELS[a.type] || a.type} — ${String(a.due_date || "").split("-").reverse().join("/")} ore ${a.due_time}`
            : `Attività creata: ${ACTIVITY_TYPE_LABELS[a.type] || a.type} — ${String(a.due_date || "").split("-").reverse().join("/")} ore ${a.due_time}`,
        type: "attività",
      })),
    ];
    return items
      .filter((i) => i.at)
      .sort((a, b) => new Date(b.at) - new Date(a.at))
      .slice(0, 30);
  })();

  if (!contact) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70" data-testid="contact-drawer-loading">
        <Loader2 size={22} className="animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70]" data-testid="contact-drawer">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} data-testid="contact-drawer-overlay" />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-[560px] flex-col border-l border-[#c9a45c]/30 bg-[#0d0d0d]" data-testid="contact-drawer-panel">
        <div className="flex items-start justify-between gap-3 border-b border-[#c9a45c]/20 p-5">
          <div className="min-w-0">
            <p className="eyebrow">Scheda cliente</p>
            <h2 className="display mt-2 truncate text-2xl" data-testid="contact-drawer-name">{contact.name}</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="border border-[#c9a45c]/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-gold-light" data-testid="contact-drawer-protocol">
                {PROTOCOL_LABELS[contact.protocol_interest] || contact.protocol_interest}
              </span>
              <span className="border border-[#c9a45c]/25 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-[#c9c6bf]" data-testid="contact-drawer-provenance">
                da {PROVENANCE_LABELS[contact.provenance] || contact.provenance}
              </span>
              {contact.is_demo && (
                <span className="border border-[#d4af37]/60 bg-[#d4af37]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-gold-light" data-testid="contact-drawer-demo">
                  Demo
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="btn btn-outline !min-h-[44px] !px-4 !text-[11px]" data-testid="contact-call-button">
                  <Phone size={13} /> Chiama
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="btn btn-outline !min-h-[44px] !px-4 !text-[11px]" data-testid="contact-email-button">
                  <Mail size={13} /> Email
                </a>
              )}
              <button onClick={copyContacts} className="btn btn-outline !min-h-[44px] !px-4 !text-[11px]" data-testid="contact-copy-button">
                <Copy size={13} /> Copia recapiti
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#929292] hover:text-cream" data-testid="contact-drawer-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 pb-10">
          {contact.deleted && (
            <div className="mb-5 border border-[#e07a5f]/40 bg-[#e07a5f]/10 p-4 text-[12px] text-[#e07a5f]" data-testid="contact-trashed-banner">
              Questo contatto è nel cestino (eliminato il {fmt(contact.deleted_at)}). Puoi recuperarlo per 30 giorni.
              <button onClick={() => action("restore", "Contatto recuperato")} disabled={busy} className="ml-2 underline" data-testid="contact-trash-restore">
                Recupera ora
              </button>
            </div>
          )}

          <label className="block text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">Percorso clienti</label>
          <select
            className="field mt-2 !py-2 !text-[13px]"
            value={contact.pipeline_status}
            onChange={(e) => changePipeline(e.target.value)}
            data-testid="contact-pipeline-select"
          >
            {PIPELINE_STATUSES.map((s) => (
              <option key={s} value={s}>{PIPELINE_LABELS[s]}</option>
            ))}
          </select>
          <label className="block text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">
            Percorso clienti
            <button
              onClick={() => setShowHint((v) => !v)}
              className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.12em] text-[#8f8f8f] hover:text-gold-light"
              data-testid="pipeline-hint-toggle"
            >
              <Info size={11} className="text-gold" /> Info
            </button>
          </label>
          {showHint && (
            <p className="mt-2 border-l border-gold/40 pl-3 text-[11px] leading-relaxed text-[#9a978f]" data-testid="pipeline-hint">
              {PIPELINE_HINT}
            </p>
          )}
          {reachedStages.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="contact-stages-reached">
              {reachedStages.map((s) => (
                <span key={s.status} className="border border-[#c9a45c]/25 px-2 py-0.5 text-[9px] uppercase tracking-[0.08em] text-[#c9c6bf]">
                  {PIPELINE_LABELS[s.status]} · {fmtDay(s.at)}
                </span>
              ))}
            </div>
          )}

          <div className="mt-4 border border-[#c9a45c]/35 bg-[#131009] p-4" data-testid="outcome-section">
            <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-gold-light">Esito consulenza / Prossimo passo</p>
            <div className="mt-3 grid gap-2">
              <input
                className="field !py-2 !text-[13px]"
                placeholder="Esito della videoconsulenza o del sopralluogo (facoltativo)…"
                value={outcomeForm.consultation_outcome}
                onChange={(e) => setOutcomeForm({ ...outcomeForm, consultation_outcome: e.target.value })}
                data-testid="contact-outcome-input"
              />
              <input
                className="field !py-2 !text-[13px]"
                placeholder="Prossimo passo concordato (facoltativo)…"
                value={outcomeForm.next_step}
                onChange={(e) => setOutcomeForm({ ...outcomeForm, next_step: e.target.value })}
                data-testid="contact-nextstep-input"
              />
              <div className="flex flex-wrap items-end gap-2">
                <select
                  value={outcomeActivity.type}
                  onChange={(e) => setOutcomeActivity({ ...outcomeActivity, type: e.target.value })}
                  className="field !w-auto !py-2 !text-[12px]"
                  data-testid="contact-outcome-activity-type"
                >
                  {ACTIVITY_TYPES.map((t) => (
                    <option key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={outcomeActivity.due_date}
                  onChange={(e) => setOutcomeActivity({ ...outcomeActivity, due_date: e.target.value })}
                  className="field !w-auto !py-2 !text-[12px]"
                  data-testid="contact-outcome-date-input"
                />
                <input
                  type="time"
                  value={outcomeActivity.due_time}
                  onChange={(e) => setOutcomeActivity({ ...outcomeActivity, due_time: e.target.value })}
                  className="field !w-auto !py-2 !text-[12px]"
                  data-testid="contact-outcome-time-input"
                />
              </div>
              <p className="text-[10px] leading-relaxed text-[#8f8f8f]">
                Con data e ora indicate, il prossimo passo viene programmato anche come attività senza creare duplicati.
              </p>
              <button
                onClick={saveOutcome}
                disabled={busy}
                className="btn btn-gold !min-h-[44px] w-full !text-[10px] disabled:opacity-60"
                data-testid="contact-outcome-save"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salva esito e prossimo passo
              </button>
            </div>
          </div>

          <ActivitiesSection contact={contact} apply={apply} focusActivityId={focusActivityId} />

          <p className="mt-6 text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">Dati cliente</p>
          <div className="mt-2 grid gap-3">
            <input className="field !py-2 !text-[13px]" placeholder="Nome e cognome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
            <input className="field !py-2 !text-[13px]" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
            <input className="field !py-2 !text-[13px]" placeholder="Telefono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
            <select className="field !py-2 !text-[12px]" value={form.protocol_interest} onChange={(e) => setForm({ ...form, protocol_interest: e.target.value })} data-testid="contact-protocol-select">
              {PROTOCOL_OPTIONS.map((p) => (
                <option key={p} value={p}>{PROTOCOL_LABELS[p]}</option>
              ))}
            </select>
            <button onClick={saveProfile} disabled={busy} className="btn btn-gold !min-h-[40px] w-full !text-[10px] disabled:opacity-60" data-testid="contact-save-button">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salva dati
            </button>
          </div>

          <p className="mt-6 text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">Note private</p>
          <NotesSection contact={contact} apply={apply} busy={busy} />

          <p className="mt-6 text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">
            Appuntamenti ({(contact.bookings || []).length})
          </p>
          <div className="mt-2 grid gap-3">
            {(contact.bookings || []).length === 0 && (
              <p className="text-[12px] text-[#777]" data-testid="contact-bookings-empty">Nessuna videoconsulenza prenotata.</p>
            )}
            {(contact.bookings || []).map((b) => (
              <BookingCard key={b.id} booking={b} onUpdated={apply} />
            ))}
          </div>

          <p className="mt-6 text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">
            Richieste dal sito ({(contact.leads || []).length})
          </p>
          <div className="mt-2 grid gap-2">
            {(contact.leads || []).length === 0 && (
              <p className="text-[12px] text-[#777]" data-testid="contact-leads-empty">Nessuna richiesta dal form.</p>
            )}
            {(contact.leads || []).map((l) => (
              <div key={l.id} className="border border-[#c9a45c]/15 bg-[#141414] p-3" data-testid={`contact-lead-${l.id}`}>
                <p className="text-[13px] text-[#dedbd4]">
                  Obiettivo: <span className="text-gold-light">{l.goal}</span>
                  {l.budget ? ` · Budget: ${l.budget}` : ""}
                </p>
                {l.message && <p className="mt-1 text-[13px] italic text-[#bdb8ad]">“{l.message}”</p>}
                <p className="mt-1 font-mono text-[10px] tracking-[0.1em] text-[#8f8f8f]">{fmt(l.created_at)}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-[9px] font-bold uppercase tracking-[0.16em] text-[#a8a5a0]">Cronologia</p>
          <div className="mt-2 border-l border-[#c9a45c]/25 pl-4" data-testid="contact-timeline">
            {timeline.map((t, i) => (
              <div key={i} className="relative py-1.5" data-testid="contact-timeline-item">
                <span className="absolute -left-[21px] top-3 h-1.5 w-1.5 rounded-full bg-gold/70" />
                <p className="text-[13px] leading-snug text-[#c9c6bf]">
                  <span className="mr-2 text-[9px] font-bold uppercase tracking-[0.1em] text-gold-light">{EVENT_TYPE_LABELS[t.type] || "Evento"}</span>
                  {t.label}
                </p>
                <p className="font-mono text-[10px] tracking-[0.1em] text-[#8f8f8f]">{fmt(t.at)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-[#c9a45c]/20 p-4">
          {contact.archived ? (
            <button onClick={() => action("restore", "Contatto ripristinato")} disabled={busy} className="btn btn-outline !min-h-[44px] !px-4 !text-[11px]" data-testid="contact-unarchive-button">
              <ArchiveRestore size={13} /> Ripristina dall'archivio
            </button>
          ) : (
            <button onClick={() => action("archive", "Contatto archiviato")} disabled={busy || contact.deleted} className="btn btn-outline !min-h-[44px] !px-4 !text-[11px] disabled:opacity-40" data-testid="contact-archive-button">
              <Archive size={13} /> Archivia
            </button>
          )}
          {contact.deleted ? (
            <button onClick={() => action("restore", "Contatto recuperato")} disabled={busy} className="btn btn-gold !min-h-[44px] !px-4 !text-[11px]" data-testid="contact-restore-button">
              <Undo2 size={13} /> Recupera dal cestino
            </button>
          ) : confirmDelete ? (
            <span className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.1em] text-[#e07a5f]">Spostare nel cestino? Recuperabile 30 giorni</span>
              <button onClick={remove} disabled={busy} className="border border-[#e07a5f]/50 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#e07a5f]" data-testid="contact-delete-confirm">
                Sì, elimina
              </button>
              <button onClick={() => setConfirmDelete(false)} className="text-[10px] uppercase tracking-[0.12em] text-[#929292]" data-testid="contact-delete-no">
                No
              </button>
            </span>
          ) : (
            <button onClick={() => setConfirmDelete(true)} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#c9c6bf] hover:text-[#e07a5f]" data-testid="contact-delete-button">
              <Trash2 size={12} /> Elimina
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}
